// PublishPage — Phase 4 of the Build flow (frontend redesign v0.2 §4).
//
// Publishing a Collection = persisting the authored draft and registering
// it into the installation so the Explore surfaces (Query / Analyst) can
// see it. Two actions:
//
//   1. Save  — write the draft + provenance to the session workspace
//              (.coframe/) via POST /sessions/{id}/save.
//   2. Register — promote the committed draft into the installation
//              (RegisterAC op) so it becomes queryable.
//
// The full installation registry / fork view lives under Collections
// (the former Management surface). This page is the focused
// publish-from-Build action.

import { useState } from "react";
import { ApiError } from "../api/client";
import type { RegisterACResult } from "../api/types";
import { useSession } from "./SessionContext";

export function PublishPage() {
  const { client, session, refreshSession } = useSession();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [savedPath, setSavedPath] = useState<string | null>(null);
  const [registerResult, setRegisterResult] = useState<RegisterACResult | null>(
    null,
  );
  const [workspaceDirName, setWorkspaceDirName] = useState("");
  const [description, setDescription] = useState("");

  if (session === null) {
    return (
      <section>
        <h1>Publish</h1>
        <p style={{ color: "#888" }}>
          Create a session on the <em>Session</em> step first. Publishing
          registers a certified Collection into the installation so Query and
          Analyst can use it.
        </p>
      </section>
    );
  }

  const committed = session.scope_phase === "committed";

  async function onSave() {
    if (session === null) return;
    setBusy(true);
    setError(null);
    setSavedPath(null);
    try {
      const r = await client.saveSession(session.session_id);
      setSavedPath(r.saved_path);
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  async function onRegister() {
    if (session === null) return;
    setBusy(true);
    setError(null);
    setRegisterResult(null);
    try {
      const inputs: Record<string, unknown> = {};
      if (workspaceDirName.trim() !== "")
        inputs.workspace_dir_name = workspaceDirName.trim();
      if (description.trim() !== "") inputs.description = description.trim();
      const r = await client.runOp(session.session_id, "RegisterAC", { inputs });
      if (!r.success || !r.result)
        throw new Error(r.error ?? "RegisterAC failed");
      setRegisterResult(r.result as unknown as RegisterACResult);
      refreshSession();
    } catch (e) {
      setError(formatError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h1>Publish</h1>
      <p style={subtitleStyle}>
        Persist this Lattice and register it onto the data source{" "}
        <code>{session.installation_id}</code> so the Explore surfaces can
        query it. Certify first — registering a Lattice with a blocking
        contradiction is fail-closed by default.
      </p>

      {error !== null && <div style={errorStyle}>{error}</div>}

      {/* ── Save ─────────────────────────────────────────────── */}
      <div style={panelStyle}>
        <h2 style={panelTitleStyle}>Save draft</h2>
        <p style={panelHintStyle}>
          Writes the draft + provenance to the session workspace
          (<code>.coframe/</code>). Safe to run anytime.
        </p>
        <button onClick={onSave} disabled={busy} style={buttonStyle}>
          {busy ? "working…" : "Save draft"}
        </button>
        {savedPath !== null && (
          <p style={okStyle}>
            ✓ Saved to <code>{savedPath}</code>
          </p>
        )}
      </div>

      {/* ── Register ─────────────────────────────────────────── */}
      <div style={panelStyle}>
        <h2 style={panelTitleStyle}>Register onto data source</h2>
        <p style={panelHintStyle}>
          Promotes the committed draft into the data source's catalog. Requires
          the scope to be <strong>committed</strong>.
        </p>
        {!committed && (
          <p style={warnStyle}>
            Scope is still <code>{session.scope_phase}</code>. Commit the scope
            (on Declare) before registering.
          </p>
        )}
        <label style={labelStyle}>
          workspace dir name <span style={{ color: "#aaa" }}>(optional)</span>
          <input
            value={workspaceDirName}
            onChange={(e) => setWorkspaceDirName(e.target.value)}
            placeholder="defaults to the Lattice name"
            style={inputStyle}
            disabled={busy}
          />
        </label>
        <label style={labelStyle}>
          description <span style={{ color: "#aaa" }}>(optional)</span>
          <input
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="what this Lattice is for"
            style={inputStyle}
            disabled={busy}
          />
        </label>
        <button
          onClick={onRegister}
          disabled={busy || !committed}
          style={buttonStyle}
        >
          {busy ? "working…" : "Register Lattice"}
        </button>
        {registerResult !== null && (
          <p style={okStyle}>
            ✓ Registered <code>{registerResult.ac_name}</code> — installation
            now has {registerResult.ac_count_after} Lattice
            {registerResult.ac_count_after === 1 ? "" : "s"}.
          </p>
        )}
      </div>
    </section>
  );
}

function formatError(e: unknown): string {
  if (e instanceof ApiError) return `${e.status}: ${e.message}`;
  if (e instanceof Error) return e.message;
  return String(e);
}

// ── Styles ────────────────────────────────────────────────────────────

const subtitleStyle: React.CSSProperties = {
  color: "#666",
  fontSize: "0.9rem",
  marginTop: "-0.25rem",
};
const panelStyle: React.CSSProperties = {
  border: "1px solid #e2e2e2",
  borderRadius: 6,
  padding: "1rem 1.15rem",
  marginTop: "1rem",
  background: "#fcfcfc",
};
const panelTitleStyle: React.CSSProperties = {
  margin: "0 0 0.25rem 0",
  fontSize: "1.05rem",
};
const panelHintStyle: React.CSSProperties = {
  margin: "0 0 0.65rem 0",
  color: "#777",
  fontSize: "0.85rem",
};
const labelStyle: React.CSSProperties = {
  display: "block",
  fontSize: "0.8rem",
  color: "#555",
  marginBottom: "0.6rem",
};
const inputStyle: React.CSSProperties = {
  display: "block",
  width: "100%",
  maxWidth: 420,
  marginTop: "0.2rem",
  padding: "0.4rem 0.5rem",
  border: "1px solid #ccc",
  borderRadius: 4,
  fontSize: "0.9rem",
};
const buttonStyle: React.CSSProperties = {
  padding: "0.45rem 0.9rem",
  border: "1px solid #0366d6",
  background: "#0366d6",
  color: "#fff",
  borderRadius: 4,
  cursor: "pointer",
  fontSize: "0.9rem",
};
const okStyle: React.CSSProperties = {
  marginTop: "0.65rem",
  color: "#1a6b3a",
  fontSize: "0.88rem",
};
const warnStyle: React.CSSProperties = {
  color: "#8a5b00",
  background: "#fff5d6",
  border: "1px solid #e6d8a8",
  borderRadius: 4,
  padding: "0.4rem 0.6rem",
  fontSize: "0.85rem",
};
const errorStyle: React.CSSProperties = {
  color: "#a01b1b",
  background: "#fdecec",
  border: "1px solid #f3b6b6",
  borderRadius: 4,
  padding: "0.5rem 0.7rem",
  margin: "0.75rem 0",
  fontSize: "0.88rem",
};
